import React from 'react';
import { Card, Badge } from '../components/ui';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useDatabase } from '../hooks/useDatabase';

const StatCard = ({ title, value, icon, trend, colorClass }) => (
  <Card className="p-4 sm:p-6">
    <div className="flex justify-between items-start">
      <div>
        <p className="text-sm font-medium text-brand-text mb-1">{title}</p>
        <h3 className="text-2xl sm:text-3xl font-bold text-brand-dark">{value}</h3>
      </div>
      <div className={`p-3 rounded-lg ${colorClass}`}>
        <SafeIcon icon={icon} className="text-xl" />
      </div>
    </div>
    {trend && (
      <div className="mt-4 flex items-center text-sm">
        <SafeIcon icon={FiIcons.FiTrendingUp} className="text-green-500 mr-1" />
        <span className="text-green-500 font-medium">{trend}</span>
        <span className="text-gray-400 ml-2">vs mes anterior</span>
      </div>
    )}
  </Card>
);

const Dashboard = () => {
  const { data: trasteros, loading: loadingT } = useDatabase('trasteros');
  const { data: reservas, loading: loadingR } = useDatabase('reservas');

  const disponibles = trasteros.filter(t => t.estado === 'disponible').length;
  const ocupados = trasteros.filter(t => t.estado === 'ocupado').length;
  const ocupacionPct = trasteros.length ? Math.round((ocupados / trasteros.length) * 100) : 0;

  const loading = loadingT || loadingR;

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-brand-dark flex items-center gap-2">
            Resumen General
            {loading && <SafeIcon icon={FiIcons.FiLoader} className="animate-spin text-brand-primary text-lg" />}
          </h2>
          <p className="text-sm sm:text-base text-brand-text">Bienvenido de nuevo al panel de control.</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <StatCard title="Trasteros Totales" value={trasteros.length} icon={FiIcons.FiBox} colorClass="bg-blue-50 text-blue-600" />
        <StatCard title="Disponibles" value={disponibles} icon={FiIcons.FiCheckCircle} colorClass="bg-brand-accent/20 text-brand-dark" />
        <StatCard title="Ocupados" value={ocupados} icon={FiIcons.FiLock} colorClass="bg-brand-dark text-white" />
        <StatCard title="Reservas Activas" value={reservas.length} icon={FiIcons.FiCalendar} colorClass="bg-purple-50 text-purple-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <Card className="p-4 sm:p-6 lg:col-span-1">
          <h3 className="text-lg font-semibold text-brand-dark mb-4">Ocupación Actual</h3>
          <div className="flex items-center justify-center py-6">
            <div className="relative w-32 h-32 sm:w-40 sm:h-40 flex items-center justify-center rounded-full border-8 border-gray-100">
              <svg className="absolute inset-0 w-full h-full transform -rotate-90">
                <circle
                  cx="50%"
                  cy="50%"
                  r="45%"
                  stroke="currentColor"
                  strokeWidth="10%"
                  fill="transparent"
                  className="text-brand-dark"
                  strokeDasharray="283"
                  strokeDashoffset={283 - (283 * ocupacionPct) / 100}
                />
              </svg>
              <span className="text-3xl sm:text-4xl font-bold text-brand-dark">{ocupacionPct}%</span>
            </div>
          </div>
          <div className="space-y-3 mt-4">
            <div className="flex justify-between items-center text-sm">
              <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-brand-dark"></span>Ocupados</span>
              <span className="font-medium">{ocupados}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-brand-accent"></span>Disponibles</span>
              <span className="font-medium">{disponibles}</span>
            </div>
          </div>
        </Card>

        <Card className="p-4 sm:p-6 lg:col-span-2">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-brand-dark">Últimas Reservas</h3>
            <a href="#/reservas" className="text-sm text-brand-primary hover:underline">Ver todas</a>
          </div>
          {loadingR ? (
            <div className="flex justify-center py-8">
              <SafeIcon icon={FiIcons.FiLoader} className="animate-spin text-brand-primary text-2xl" />
            </div>
          ) : reservas.length === 0 ? (
            <p className="text-center text-brand-text py-8 text-sm">No hay reservas registradas.</p>
          ) : (
            <div className="overflow-x-auto hide-scrollbar">
              <table className="w-full text-left text-sm min-w-[400px]">
                <thead>
                  <tr className="border-b border-gray-100 text-brand-text">
                    <th className="pb-3 font-medium px-2">Nº Reserva</th>
                    <th className="pb-3 font-medium px-2">Entrada Prevista</th>
                    <th className="pb-3 font-medium px-2">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {reservas.slice(0, 5).map(r => (
                    <tr key={r.id} className="hover:bg-gray-50/50">
                      <td className="py-3 font-medium text-brand-dark px-2">{r.numero_reserva}</td>
                      <td className="py-3 text-brand-text px-2">{r.fecha_entrada_prevista || '—'}</td>
                      <td className="py-3 px-2"><Badge variant={r.estado}>{r.estado}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;